import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { useNumbers } from "@shared/hooks/use-numbers";
import {
  useSettingsQuery,
  useUpdateSetting,
} from "@/features/settings/useSettings";
import { createContext, useContext } from "react";
import { useAuth } from "@/features/auth/contexts/AuthContext";

// Define the shape of our settings
const defaultSettings = {
  siteName: "delivery",
  siteDescription: "Site description test",
  contactEmail: "admin@wk.ma",
};

// Create context outside the component to avoid recreation on each render
const SettingsContext = createContext({
  settings: defaultSettings,
  updateSetting: () => {},
  resetSettings: () => {},
  isPending: false,
  isLoading: false,
  numbers: {
    packages: 0,
    pickups: 0,
    users: 0,
  },
  numbersLoading: false,
  numbersError: false,
});

// Custom hook for using settings
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

// Settings Provider component
const SettingsProvider = ({ children }) => {
  const { data: settings = defaultSettings, isLoading } = useSettingsQuery();
  const { mutate: updateSetting, isPending } = useUpdateSetting();
  const { isAuthenticated } = useAuth();

  // Use the dedicated numbers hook for better organization and reusability
  const {
    numbers,
    isLoading: numbersLoading,
    hasError: numbersError,
    loadingStates,
    errors,
  } = useNumbers({
    enabled: isAuthenticated,
  });

  // Show loading state only when both settings and numbers are loading initially
  const shouldShowLoader = isLoading && numbersLoading;

  if (shouldShowLoader) {
    return (
      <div className="flex items-center justify-center h-screen">
        <ThreeDotLoader />
      </div>
    );
  }

  return (
    <SettingsContext.Provider
      value={{
        settings,
        isPending,
        numbers,
        numbersLoading,
        numbersError,
        numbersLoadingStates: loadingStates,
        numbersErrors: errors,
        isLoading,
        updateSetting,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsProvider;
